import { Box, Paper, Typography } from "@mui/material";
import { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { SECONDS_IN_DAY } from "components/manualReview/utils/timeUtils";
import { memo, useCallback, useMemo, useRef } from "react";
import {
  HeatSummary,
  LocationExceptionalStatusRead,
  LocationReviewPeriods,
  ReviewPeriodData,
  ReviewTimelineTick,
} from "src/store/api/generatedApi";
import { CollapsedLocationLane } from "./CollapsedLocationLane";
import { isDragGesture, PointerPosition } from "./dragGesture";
import { ExpandedLocationLane } from "./ExpandedLocationLane";
import { HourRailLabels, HourRails } from "./HourRails";
import { LoadingInterval } from "./locationLoadingIntervals";
import {
  laneHeight,
  LANE_GAP,
  STACK_FOOTER_HEIGHT,
  STACK_HOUR_HEADER_HEIGHT,
  STACK_LABEL_GUTTER,
} from "./timelineLayout";
import { useTimelineScrub } from "./useTimelineScrub";
import { ViewingWindowIndicator } from "./ViewingWindowIndicator";
import { ViewingWindowTimeLabel } from "./ViewingWindowTimeLabel";

const LaneSeparators = memo(({ offsets }: { offsets: readonly number[] }) => (
  <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
    {offsets.map((top) => (
      <Box
        key={top}
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top,
          height: "1px",
          bgcolor: "divider",
          opacity: 0.5,
        }}
      />
    ))}
  </Box>
));
LaneSeparators.displayName = "LaneSeparators";

export const hasExpandableLane = (
  locations: readonly LocationReviewPeriods[],
  expandedLocation: string
): boolean =>
  locations.some(
    ({ location_name: locationName }) => locationName === expandedLocation
  );

const EMPTY_EXCEPTIONAL_STATUSES: LocationExceptionalStatusRead[] = [];
const EMPTY_LOADING_INTERVALS: readonly LoadingInterval[] = [];

interface LocationTimelineStackProps {
  locations: LocationReviewPeriods[];
  expandedLocation: string;
  viewingWindowStart: Dayjs;
  viewingWindowEnd: Dayjs;
  heats: HeatSummary[];
  ticks: ReviewTimelineTick[];
  expandedReviewPeriods: ReviewPeriodData[];
  exceptionalStatusesByLocation: Map<string, LocationExceptionalStatusRead[]>;
  loadingIntervalsByLocation: Map<string, LoadingInterval[]>;
  onWindowChange: (newStart: Dayjs) => void;
  onOpenLocation: (location: string, start: Dayjs) => void;
  dayStart: Dayjs;
  dayEnd: Dayjs;
}

export const LocationTimelineStack = ({
  locations,
  expandedLocation,
  viewingWindowStart,
  viewingWindowEnd,
  heats,
  ticks,
  expandedReviewPeriods,
  exceptionalStatusesByLocation,
  loadingIntervalsByLocation,
  onWindowChange,
  onOpenLocation,
  dayStart,
  dayEnd,
}: LocationTimelineStackProps) => {
  const { t } = useTenantTranslation();
  const plotAreaRef = useRef<HTMLDivElement>(null);
  const gestureOriginRef = useRef<PointerPosition | null>(null);

  const getPositionPercent = useCallback(
    (time: Dayjs): number =>
      (time.diff(dayStart, "second") / SECONDS_IN_DAY) * 100,
    [dayStart]
  );

  const {
    isScrubbing,
    calculateClampedStart,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
  } = useTimelineScrub({ plotAreaRef, dayStart, dayEnd, onWindowChange });

  const recordGestureOrigin = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      gestureOriginRef.current = { x: event.clientX, y: event.clientY };
    },
    []
  );

  const handleLaneClick = useCallback(
    (location: string, event: React.MouseEvent<HTMLElement>) => {
      if (
        isDragGesture(gestureOriginRef.current, {
          x: event.clientX,
          y: event.clientY,
        })
      ) {
        return;
      }
      const clampedStart = calculateClampedStart(event.clientX);
      if (clampedStart) {
        onOpenLocation(location, clampedStart);
      }
    },
    [calculateClampedStart, onOpenLocation]
  );

  const handleLaneKeyDown = useCallback(
    (location: string, event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      onOpenLocation(location, viewingWindowStart);
    },
    [onOpenLocation, viewingWindowStart]
  );

  const separatorOffsets = useMemo(
    () =>
      locations
        .slice(0, -1)
        .reduce<number[]>(
          (offsets, { location_name: name }) => [
            ...offsets,
            (offsets.at(-1) ?? -LANE_GAP / 2) +
              laneHeight(name === expandedLocation) +
              LANE_GAP,
          ],
          []
        ),
    [locations, expandedLocation]
  );

  const windowLeftPercent = getPositionPercent(viewingWindowStart);
  const windowWidthPercent =
    getPositionPercent(viewingWindowEnd) - windowLeftPercent;

  return (
    <Box sx={{ mb: 2 }}>
      <Paper sx={{ userSelect: "none", overflow: "hidden" }}>
        <Box sx={{ display: "flex" }}>
          <Box sx={{ width: STACK_LABEL_GUTTER, flexShrink: 0 }} />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              position: "relative",
              height: STACK_HOUR_HEADER_HEIGHT,
            }}
          >
            <HourRailLabels dayStart={dayStart} />
          </Box>
        </Box>
        <Box sx={{ display: "flex", position: "relative" }}>
          <LaneSeparators offsets={separatorOffsets} />
          <Box
            sx={{
              width: STACK_LABEL_GUTTER,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: `${LANE_GAP}px`,
            }}
          >
            {locations.map(({ location_name: locationName }) => (
              <Box
                key={locationName}
                sx={{
                  height: laneHeight(locationName === expandedLocation),
                  display: "flex",
                  alignItems: "center",
                  overflow: "hidden",
                  pl: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight:
                      locationName === expandedLocation
                        ? "fontWeightBold"
                        : "fontWeightRegular",
                    color:
                      locationName === expandedLocation
                        ? "text.primary"
                        : "text.secondary",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {locationName}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            ref={plotAreaRef}
            onMouseDown={recordGestureOrigin}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            sx={{
              flex: 1,
              minWidth: 0,
              position: "relative",
              display: "flex",
              flexDirection: "column",
              gap: `${LANE_GAP}px`,
            }}
          >
            <HourRails />
            {locations.map(
              ({ location_name: locationName, review_periods }) => {
                const isExpanded = locationName === expandedLocation;
                return (
                  <Box
                    key={locationName}
                    onMouseDown={isExpanded ? handleMouseDown : undefined}
                    onClick={
                      isExpanded
                        ? undefined
                        : (event) => {
                            handleLaneClick(locationName, event);
                          }
                    }
                    onKeyDown={
                      isExpanded
                        ? undefined
                        : (event) => {
                            handleLaneKeyDown(locationName, event);
                          }
                    }
                    role={isExpanded ? undefined : "button"}
                    tabIndex={isExpanded ? undefined : 0}
                    aria-label={
                      isExpanded
                        ? undefined
                        : t("openCraneInNewTab", { location: locationName })
                    }
                    sx={{
                      position: "relative",
                      height: laneHeight(isExpanded),
                      "&:focus-visible": {
                        outline: 2,
                        outlineColor: "primary.main",
                        outlineOffset: -2,
                      },
                      overflowX: "hidden",
                      cursor: isExpanded
                        ? isScrubbing
                          ? "grabbing"
                          : "grab"
                        : "pointer",
                      ...(isExpanded
                        ? {}
                        : { "&:hover": { bgcolor: "action.hover" } }),
                    }}
                  >
                    {isExpanded ? (
                      <ExpandedLocationLane
                        heats={heats}
                        ticks={ticks}
                        reviewPeriods={expandedReviewPeriods}
                        exceptionalStatuses={
                          exceptionalStatusesByLocation.get(locationName) ??
                          EMPTY_EXCEPTIONAL_STATUSES
                        }
                        loadingIntervals={
                          loadingIntervalsByLocation.get(locationName) ??
                          EMPTY_LOADING_INTERVALS
                        }
                        dayStart={dayStart}
                        getPositionPercent={getPositionPercent}
                      />
                    ) : (
                      <CollapsedLocationLane
                        reviewPeriods={review_periods}
                        exceptionalStatuses={
                          exceptionalStatusesByLocation.get(locationName) ??
                          EMPTY_EXCEPTIONAL_STATUSES
                        }
                        loadingIntervals={
                          loadingIntervalsByLocation.get(locationName) ??
                          EMPTY_LOADING_INTERVALS
                        }
                        getPositionPercent={getPositionPercent}
                      />
                    )}
                  </Box>
                );
              }
            )}
            <ViewingWindowIndicator
              windowLeftPercent={windowLeftPercent}
              windowWidthPercent={windowWidthPercent}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex" }}>
          <Box sx={{ width: STACK_LABEL_GUTTER, flexShrink: 0 }} />
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              position: "relative",
              height: STACK_FOOTER_HEIGHT,
            }}
          >
            <ViewingWindowTimeLabel
              windowLeftPercent={windowLeftPercent}
              viewingWindowStart={viewingWindowStart}
              viewingWindowEnd={viewingWindowEnd}
              top={0}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
